import { computed, ref } from 'vue'
import { useToast } from 'primevue/usetoast'
import { defineStore } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { useAuthService } from '@/services/auth/useAuthService'
import { useWebSocketStore } from '@/components/whatsapp/stores/useWebSocketStore'
import { useWhatsAppCoordinator } from '@/components/whatsapp/stores/useWhatsAppCoordinator'
import { useCallService } from '@/services/call/useCallService'
import { useCampaignService } from '@/services/campaign/useCampaignService'
import { clearPauseCache } from '@/services/pause/usePauseService'
import { TypeCampaign } from '@/services/campaign/enums/type-campaign.enum'
import { useWorkspaceStore } from '@/stores/useWorkspaceStore'
import { useAiCreditsStore } from '@/stores/useAiCreditsStore'
import { useCallAiStore } from '@/stores/useCallAiStore'
import { useCampaignRegistrationStore } from '@/stores/useCampaignRegistrationStore'
import { usePauseStatusStore } from '@/stores/usePauseStatusStore'
import { useShortcutStore } from '@/stores/useShortcutStore'
import { useWhatsappAiStore } from '@/stores/useWhatsappAiStore'
import { useWhatsAppNotificationStore } from '@/stores/useWhatsAppNotificationStore'
import type { IUser, ILoginCredentials } from './auth/types'
import type { ISessionStatusResponse } from '@/services/auth/interfaces'

type ExtensionData = {
  number?: string
  description?: string
  [key: string]: unknown
} | null

type SessionStatusOptions = {
  force?: boolean
}

type LoginOptions = {
  redirect?: boolean
}

export type LogoutChatOutcome = 'keep_open' | 'release' | 'mixed' | 'none'

type SessionStatusRequest = {
  id: number
  promise: Promise<ISessionStatusResponse>
}

export const useAuthStore = defineStore('auth', () => {
  const authService = useAuthService()
  const callService = useCallService()
  const campaignService = useCampaignService()
  const toast = useToast()
  const router = useRouter()
  const { t } = useI18n({ useScope: 'global' })
  let logoutPromise: Promise<void> | null = null
  let isLoggingOut = false
  let sessionStatusRequestId = 0
  const sessionStatusRequests = new Map<string, SessionStatusRequest>()

  const getStoredExtension = (): ExtensionData => {
    const ext = localStorage.getItem('oper_extension')
    if (!ext) return null

    try {
      return JSON.parse(ext)
    } catch {
      return null
    }
  }

  const user = ref<IUser | null>(
    localStorage.getItem('oper_user_data')
      ? JSON.parse(localStorage.getItem('oper_user_data')!)
      : null,
  )
  const accessToken = ref<string | null>(localStorage.getItem('oper_access_token') || null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const showWebphone = ref(false)
  const extensionIsActive = ref(localStorage.getItem('oper_extension_is_active') === 'true')

  const extension = ref<ExtensionData>(getStoredExtension())
  const webphoneAvailable = ref(true)

  const isAuthenticated = computed(() => !!accessToken.value && !!user.value)
  const isLoggedIn = computed(() => isAuthenticated.value)
  const currentUser = computed(() => user.value)
  const hasError = computed(() => !!error.value)
  const userId = computed(() => user.value?.id || null)
  const userRole = computed(() => 'agent' as const)
  const userExtension = computed(() => user.value?.extension || '')
  const userName = computed(() => user.value?.name || '')
  const userAlias = computed(() => user.value?.alias || '')

  const extensionNumber = computed(() => extension.value?.number || 'N/A')
  const extensionDescription = computed(() => extension.value?.description || '-')
  const isExtensionUnavailable = computed(
    () => !webphoneAvailable.value || extensionIsActive.value,
  )
  const canOpenWebphone = computed(
    () => Boolean(userExtension.value) && webphoneAvailable.value && !extensionIsActive.value,
  )

  const setAuthData = (newUser: IUser, newToken: string, sessionIdentifier?: string) => {
    user.value = newUser
    accessToken.value = newToken
    error.value = null

    localStorage.setItem('oper_access_token', newToken)
    localStorage.setItem('oper_user_data', JSON.stringify(newUser))

    if (sessionIdentifier) {
      localStorage.setItem('oper_session_identifier', sessionIdentifier)
    }
  }

  const setExtensionData = (newExtension: ExtensionData) => {
    extension.value = newExtension

    if (newExtension) {
      localStorage.setItem('oper_extension', JSON.stringify(newExtension))
    } else {
      localStorage.removeItem('oper_extension')
    }
  }

  const setWebphoneAvailability = (available: boolean) => {
    webphoneAvailable.value = available

    if (!available) {
      showWebphone.value = false
    }
  }

  const setExtensionIsActive = (isActive: boolean) => {
    extensionIsActive.value = isActive

    if (isActive) {
      showWebphone.value = false
      localStorage.setItem('oper_extension_is_active', 'true')
    } else {
      localStorage.removeItem('oper_extension_is_active')
    }
  }

  /**
   * Limpia la sesión de ESTA pestaña.
   *
   * `localStorage` es del navegador, no de la pestaña, y el interceptor de
   * axios lee el token de ahí en cada petición. Si otra pestaña ya inició
   * sesión y escribió el suyo, borrarlo la dejaría sin credenciales a media
   * jornada: el estado en memoria seguiría pareciendo válido y cada petición
   * respondería 401. Por eso las claves compartidas solo se borran cuando
   * siguen siendo las de esta pestaña.
   */
  const clearAuthData = () => {
    campaignService.clearCampaignCache()
    clearPauseCache()
    sessionStatusRequests.clear()
    useWorkspaceStore().$reset()

    const ownToken = accessToken.value

    user.value = null
    accessToken.value = null
    error.value = null
    extension.value = null
    webphoneAvailable.value = true
    extensionIsActive.value = false

    const storedToken = localStorage.getItem('oper_access_token')
    if (storedToken !== null && storedToken !== ownToken) return

    localStorage.removeItem('oper_access_token')
    localStorage.removeItem('oper_user_data')
    localStorage.removeItem('oper_extension')
    localStorage.removeItem('oper_session_identifier')
    localStorage.removeItem('oper_extension_is_active')
  }

  const clearCampaignSessionState = () => {
    // El registro en campaña ya no se persiste: la voz la confirma el heartbeat
    // de QueueMemberGrouped y el chat se consulta contra campaign_logins.
    localStorage.setItem('operator_queue_paused', 'false')
    localStorage.removeItem('operator_queue_pause_id')
    localStorage.removeItem('operator_queue_pause_reason')
    localStorage.removeItem('operator_queue_pause_started_at')
  }

  const terminateActivePauseBeforeLogout = async () => {
    const pauseStatusStore = usePauseStatusStore()
    if (!pauseStatusStore.isPaused) return

    const activePauseId = pauseStatusStore.selectedPauseId
    const activePauseName =
      pauseStatusStore.selectedPause?.name || pauseStatusStore.pauseReasonLabel || ''
    const activePauseDescription = pauseStatusStore.selectedPause?.description || ''

    try {
      if (user.value?.id && activePauseId) {
        const result = await callService.pauseQueues(
          {
            user_id: user.value.id,
            pause: {
              id: activePauseId,
              name: activePauseName,
              description: activePauseDescription,
            },
            is_paused: false,
            extension: user.value.extension || extension.value?.number || '',
            protocol: 'PJSIP',
          },
          { notify: false },
        )

        if (!result.status) {
          console.error('[useAuthStore] no se pudo despausar antes del logout')
        }
      }
    } catch (error) {
      // No lanza: cerrar sesión saca al agente de todo — colas, registro y
      // pausa —, así que un fallo acá no puede dejarlo atrapado en una sesión
      // que ya dio por terminada. El pause_log lo cierra el backend en la baja
      // de campañas, que corre después.
      console.error('[useAuthStore] error despausando antes del logout:', error)
    } finally {
      pauseStatusStore.clearPause()
    }
  }

  const resetSessionStores = () => {
    const workspace = useWorkspaceStore()
    const campaignRegistrationStore = useCampaignRegistrationStore()
    const pauseStatusStore = usePauseStatusStore()
    const aiCreditsStore = useAiCreditsStore()
    const callAiStore = useCallAiStore()
    const shortcutStore = useShortcutStore()
    const whatsappAiStore = useWhatsappAiStore()
    const whatsappNotificationStore = useWhatsAppNotificationStore()
    const whatsappCoordinator = useWhatsAppCoordinator()

    whatsappCoordinator.disconnect()
    campaignRegistrationStore.resetSessionState()
    pauseStatusStore.clearPause()
    aiCreditsStore.clearCredits()
    callAiStore.resetCallAiData()
    callAiStore.endCallSession()
    shortcutStore.clearShortcuts()
    whatsappAiStore.clearInsights()
    whatsappNotificationStore.resetUnreadCount()
    whatsappNotificationStore.setInitialized(false)
    workspace.$reset()
    clearCampaignSessionState()
  }

  const setLoading = (loadingState: boolean) => {
    loading.value = loadingState
  }

  const setError = (errorMessage: string | null) => {
    error.value = errorMessage
  }

  /**
   * El estado de sesión no se cachea. Decide si el agente entra, con qué
   * extensión y en qué campañas, y las tres cosas pueden cambiar en el minuto
   * anterior al login: el caché de una hora en sessionStorage respondía con la
   * foto vieja y el agente entraba con datos que ya no eran los suyos.
   *
   * Lo único que se comparte es la petición en vuelo, para que varios
   * llamadores simultáneos no disparen la misma consulta (AppHeader y
   * AppSidebarMobile están montados a la vez). `force` salta también esa
   * deduplicación: después de una mutación (registro, baja, logout) unirse a
   * una petición que salió antes devolvería el estado previo.
   */
  const checkSessionStatus = async (
    emailOrUsername: string,
    options: SessionStatusOptions = {},
  ): Promise<ISessionStatusResponse> => {
    const sessionIdentifier = emailOrUsername.trim()

    if (options.force) {
      sessionStatusRequests.delete(sessionIdentifier)
    } else {
      const pendingRequest = sessionStatusRequests.get(sessionIdentifier)
      if (pendingRequest) return pendingRequest.promise
    }

    try {
      setLoading(true)
      setError(null)

      const requestId = ++sessionStatusRequestId
      const requestedAt = Date.now()
      const request = authService
        .sessionStatus(sessionIdentifier)
        .then((response) => {
          if (!response.has_session && isAuthenticated.value && !isLoggingOut) {
            closeInvalidSession()
          } else if (response.has_session && isAuthenticated.value && !isLoggingOut) {
            usePauseStatusStore().syncWithBackend(response.active_pause, requestedAt)
          }

          return response
        })
        .finally(() => {
          if (sessionStatusRequests.get(sessionIdentifier)?.id === requestId) {
            sessionStatusRequests.delete(sessionIdentifier)
          }
        })

      sessionStatusRequests.set(sessionIdentifier, { id: requestId, promise: request })
      return await request
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('auth.session_check_error')
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const getSessionIdentifier = () => {
    return (
      localStorage.getItem('oper_session_identifier') ||
      user.value?.email ||
      user.value?.alias ||
      ''
    )
  }

  /**
   * Cierra la sesión SOLO en local: no llama al backend a propósito.
   *
   * Se usa cuando el backend ya dio la sesión por terminada. Un logout normal
   * aquí sería destructivo: `/oper/auth/logout` cierra todos los `user_logs`
   * abiertos del usuario, así que el navegador desplazado cerraría la sesión
   * del que acaba de entrar.
   *
   * `notice` elige el aviso: desplazamiento (el agente entró en otro lado) o
   * sesión cerrada por el backend tras la gracia de desconexión. Sin `notice`,
   * una sesión que simplemente caducó, que no necesita explicación.
   */
  const closeInvalidSession = (
    options: { notice?: 'session_replaced' | 'session_expired' } = {},
  ) => {
    // La sesión ya no es válida: el estado de registro del usuario anterior no
    // puede sobrevivir al siguiente login en el mismo navegador.
    useCampaignRegistrationStore().resetSessionState()
    clearAuthData()

    if (router.currentRoute.value.name !== 'sign-in') {
      router.push({ name: 'sign-in' })
    }

    if (options.notice === 'session_replaced') {
      toast.add({
        severity: 'warn',
        summary: t('auth.session_replaced_header'),
        detail: t('auth.session_replaced_body'),
        life: 8000,
      })
    }

    if (options.notice === 'session_expired') {
      toast.add({
        severity: 'warn',
        summary: t('auth.session_expired_header'),
        detail: t('auth.session_expired_body'),
        life: 8000,
      })
    }
  }

  const validateActiveSession = async (
    emailOrUsername = getSessionIdentifier(),
    options: SessionStatusOptions = {},
  ): Promise<boolean> => {
    if (!emailOrUsername) {
      closeInvalidSession()
      return false
    }

    try {
      const response = await checkSessionStatus(emailOrUsername, options)

      if (!response.has_session) {
        closeInvalidSession()
        return false
      }

      return true
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('auth.session_check_error')
      setError(errorMessage)
      throw err
    }
  }

  const getLogoutChatOutcome = async (): Promise<LogoutChatOutcome> => {
    const sessionIdentifier = getSessionIdentifier()
    if (!sessionIdentifier) return 'none'

    try {
      const sessionStatus = await checkSessionStatus(sessionIdentifier, { force: true })
      if (!sessionStatus.has_session) return 'none'

      const relevantCampaigns = sessionStatus.campaigns.filter(
        (campaign) => campaign.is_logged_in && campaign.type === TypeCampaign.CHAT,
      )
      if (!relevantCampaigns.length) return 'none'

      const keepOpenFlags = relevantCampaigns.map(
        (campaign) => campaign.config_action?.keep_agent_chats_on_logout_enabled ?? false,
      )

      if (keepOpenFlags.every((flag) => flag)) return 'keep_open'
      if (keepOpenFlags.every((flag) => !flag)) return 'release'
      return 'mixed'
    } catch {
      return 'none'
    }
  }

  /**
   * Saca al agente de sus campañas al cerrar sesión. No lanza nunca.
   *
   * Manda solo el user_id: el backend resuelve el alcance leyendo
   * campaign_logins y saca de TODAS las campañas. Antes esto se calculaba acá a
   * partir de session-status y, si esa consulta fallaba, la baja no se llamaba
   * o se llamaba sin alguna cola — el agente se deslogueaba y Asterisk le
   * seguía repartiendo llamadas.
   */
  const unregisterCampaignsOnLogout = async () => {
    if (!user.value?.id) return

    try {
      await callService.unregisterOperatorSession(
        { user_id: user.value.id },
        { notify: false },
      )
    } catch (error) {
      // Un fallo acá no puede impedir que la sesión local se cierre: el agente
      // quedaría atrapado en una sesión que el backend ya dio por terminada.
      console.error('[useAuthStore] no se pudo cerrar el registro de campañas:', error)
    } finally {
      clearCampaignSessionState()
    }
  }

  const redirectAfterLogin = () => {
    const redirectPath = localStorage.getItem('redirectAfterLogin')
    if (redirectPath) {
      localStorage.removeItem('redirectAfterLogin')
      router.push(redirectPath)
    } else {
      router.push({ name: 'home' })
    }
  }

  const login = async (credentials: ILoginCredentials, options: LoginOptions = {}) => {
    try {
      setLoading(true)
      setError(null)

      const response = await authService.login(credentials)

      setExtensionIsActive(response.extension_is_active === true)

      const userData: IUser = {
        id: response.id,
        name: response.name,
        alias: response.alias,
        extension: response.extension,
        password_extension: response.password_extension,
        avatar: response.avatar || undefined,
        email: credentials.username.includes('@') ? credentials.username : undefined,
      }

      setAuthData(userData, response.access_token, credentials.username)

      setExtensionData({
        number: response.extension || 'N/A',
        description: response.alias || response.name || '-',
      })

      toast.add({
        severity: 'success',
        summary: t('auth.successful_login'),
        life: 3000,
      })

      if (options.redirect !== false) redirectAfterLogin()

      return response
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('auth.incorrect_credentials')
      setError(errorMessage)

      throw err
    } finally {
      setLoading(false)
    }
  }

  const executeLogout = async () => {
    isLoggingOut = true
    try {
      setLoading(true)

      const webSocketStore = useWebSocketStore()
      if (webSocketStore.isConnected && user.value?.id) {
        webSocketStore.sendAgentLogout(user.value.id)
      }

      await terminateActivePauseBeforeLogout()

      try {
        if (user.value?.alias) {
          await authService.logout(user.value.alias)
        }
      } finally {
        // Después de cerrar la sesión y pase lo que pase con ella: dejar al
        // agente registrado en campañas es peor que un logout a medias, porque
        // Asterisk seguiría enviándole llamadas a una sesión que ya no existe.
        await unregisterCampaignsOnLogout()
      }

      resetSessionStores()
      clearAuthData()

      toast.add({
        severity: 'info',
        summary: t('auth.sesion_closed'),
        life: 3000,
      })

      router.push({ name: 'sign-in' })
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('auth.logout_error')
      setError(errorMessage)

      toast.add({
        severity: 'error',
        summary: t('auth.logout_error'),
        detail: errorMessage,
        life: 3000,
      })

      throw err
    } finally {
      isLoggingOut = false
      setLoading(false)
    }
  }

  const logout = async () => {
    if (logoutPromise) return logoutPromise

    logoutPromise = executeLogout().finally(() => {
      logoutPromise = null
    })

    return logoutPromise
  }

  const generateOtp = async (email: string) => {
    try {
      setLoading(true)
      setError(null)

      await authService.generateOtp({ email })
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('auth.otp_generation_error')
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const verifyOtp = async (email: string, code: string) => {
    try {
      setLoading(true)
      setError(null)

      await authService.verifyOtp({ email, code })
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('auth.otp_verification_error')
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const initializeAuth = async () => {
    const token = localStorage.getItem('oper_access_token')
    const userData = localStorage.getItem('oper_user_data')
    const extensionData = localStorage.getItem('oper_extension')

    if (token && userData) {
      try {
        accessToken.value = token
        user.value = JSON.parse(userData)

        if (extensionData) {
          extension.value = JSON.parse(extensionData)
        }
      } catch {
        clearAuthData()
        return
      }

      try {
        await validateActiveSession()
      } catch {
        // Mantiene la sesión local si falla la petición; solo has_session=false cierra sesión.
      }
    }
  }

  const toggleWebphone = () => {
    if (!showWebphone.value && !canOpenWebphone.value) return

    showWebphone.value = !showWebphone.value
  }

  const setShowWebphone = (value: boolean) => {
    if (value && !canOpenWebphone.value) return

    showWebphone.value = value
  }

  return {
    user,
    accessToken,
    loading,
    error,
    showWebphone,
    extension,
    webphoneAvailable,
    extensionIsActive,

    isAuthenticated,
    isLoggedIn,
    currentUser,
    hasError,
    userId,
    userRole,
    userExtension,
    userName,
    userAlias,
    extensionNumber,
    extensionDescription,
    isExtensionUnavailable,
    canOpenWebphone,

    setAuthData,
    setExtensionData,
    setWebphoneAvailability,
    setExtensionIsActive,
    clearAuthData,
    closeInvalidSession,
    setLoading,
    setError,
    checkSessionStatus,
    validateActiveSession,
    getLogoutChatOutcome,
    login,
    redirectAfterLogin,
    logout,
    generateOtp,
    verifyOtp,
    initializeAuth,
    toggleWebphone,
    setShowWebphone,
  }
})
